import { AcknowledgmentResponseStatus } from '../../../services/socket/SocketEvents.tsx';
import { DocumentSettingsEvents } from '../../../services/socket/SocketEvents.tsx';
import type { DocumentSettingsType } from '../SidePanel/DocumentSettings/DocumentDesignSettings/DocumentSettingsContext.tsx';
import type { AllTextStyleState, TableTextDefaultStylesState, TextDefaultStylesState } from '../grid/reduxStore/textDefaultStylingSlice.ts';
import { setTableTextDefaultStyling, setTextDefaultStyling } from '../grid/reduxStore/textDefaultStylingSlice.ts';
import type { TableRowStyleState } from '../grid/reduxStore/tableRowDefaultStyleSlice.ts';
import { setTableRowStyle } from '../grid/reduxStore/tableRowDefaultStyleSlice.ts';
import { setCurrencySettings } from '../grid/reduxStore/documentSettingsSlice.ts';
import type { CommandBaseDependencies } from './CommandBase.ts';
import { CommandBase } from './CommandBase.ts';
import { setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice.ts';
import { updateGridPositionConfig } from '../grid/reduxStore/editorSlice.ts';
import { updateBlockPositionViaSocket } from '../grid/reduxStore/saveHandlers.ts';

export enum BlockPositionSource {
  Original = 'original',
  Clamped = 'clamped',
}

export type MovedBlock = {
  blockId: string;
  sectionId: string;
  originalX: number;
  clampedX: number;
  y: number;
};

export interface DocumentSettingsChangePayload {
  documentSettings?: DocumentSettingsType;
  textDefaultStyles?: TextDefaultStylesState;
  tableTextDefaultStyles?: TableTextDefaultStylesState;
  allTextDefaultStyles?: AllTextStyleState;
  tableRowStyles?: TableRowStyleState;
}

export interface UpdateDocumentSettingsCommandParams {
  previousState: DocumentSettingsChangePayload;
  newState: DocumentSettingsChangePayload;
  updateLocalState?: (settings: DocumentSettingsType) => void;
  movedBlocks?: MovedBlock[];
  userId?: number;
}

export class UpdateDocumentSettingsCommand extends CommandBase {
  readonly scope = 'document' as const;

  private readonly previousState: DocumentSettingsChangePayload;
  private readonly newState: DocumentSettingsChangePayload;
  private readonly updateLocalState?: (settings: DocumentSettingsType) => void;
  private readonly movedBlocks: MovedBlock[];
  private readonly userId?: number;

  constructor(dependencies: CommandBaseDependencies, params: UpdateDocumentSettingsCommandParams) {
    super(dependencies);
    this.previousState = structuredClone(params.previousState);
    this.newState = structuredClone(params.newState);
    this.updateLocalState = params.updateLocalState;
    this.movedBlocks = structuredClone(params.movedBlocks ?? []);
    this.userId = params.userId;
  }

  async execute(): Promise<boolean> {
    return this.applyAndPersist(this.newState, this.previousState);
  }

  async undo(): Promise<boolean> {
    const success = await this.applyAndPersist(this.previousState, this.newState);
    if (success) {
      await this.restoreBlockPositions(BlockPositionSource.Original);
    }
    return success;
  }

  async redo(): Promise<boolean> {
    const success = await this.applyAndPersist(this.newState, this.previousState);
    if (success) {
      await this.restoreBlockPositions(BlockPositionSource.Clamped);
    }
    return success;
  }

  private async restoreBlockPositions(which: BlockPositionSource): Promise<void> {
    if (!this.movedBlocks.length || this.userId == null) return;

    await Promise.all(
      this.movedBlocks.map(async (block) => {
        const x = which === BlockPositionSource.Original ? block.originalX : block.clampedX;

        this.dispatch(updateGridPositionConfig({ sectionId: block.sectionId, blockId: block.blockId, x, y: block.y }));

        await updateBlockPositionViaSocket(this.socketClient, {
          blockId: block.blockId,
          userId: this.userId as number,
          position: { top_px: block.y, left_px: x },
        }).catch(() => undefined);
      })
    );
  }

  private async applyAndPersist(state: DocumentSettingsChangePayload, rollbackState: DocumentSettingsChangePayload): Promise<boolean> {
    try {
      this.setSaving();

      this.applyToState(state);

      const socketPayload = this.buildSocketPayload(state);
      const response = await this.publishToSocket(socketPayload);

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.applyToState(rollbackState);
        this.setNotSaved();
        return false;
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.applyToState(rollbackState);
        this.setNotSaved();
        return false;
      }

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: response.status,
          errorCode: response.errorCode,
        })
      );

      return true;
    } catch (error) {
      console.error('UpdateDocumentSettingsCommand: failed', error);
      this.applyToState(rollbackState);
      this.setNotSaved();
      return false;
    }
  }

  private applyToState(state: DocumentSettingsChangePayload): void {
    if (state.documentSettings && this.updateLocalState) {
      this.updateLocalState(state.documentSettings);
    }

    if (state.textDefaultStyles) {
      this.dispatch(setTextDefaultStyling(state.textDefaultStyles));
    }

    if (state.tableTextDefaultStyles) {
      this.dispatch(setTableTextDefaultStyling(state.tableTextDefaultStyles));
    }

    if (state.tableRowStyles) {
      this.dispatch(setTableRowStyle(state.tableRowStyles));
    }

    if (state?.documentSettings?.currencySettings) {
      this.dispatch(
        setCurrencySettings({
          code: state?.documentSettings.currencySettings.code,
          symbol: state?.documentSettings.currencySettings.symbol,
        })
      );
    }
  }

  private buildSocketPayload(state: DocumentSettingsChangePayload): Record<string, any> {
    const payload: Record<string, any> = {};

    if (state.documentSettings) {
      const { currencySettings: _, ...marginAndBg } = state.documentSettings;
      Object.assign(payload, marginAndBg);

      if (state.documentSettings.currencySettings) {
        payload.currencySettings = {
          currencyCode: state.documentSettings.currencySettings.code,
          currencySign: state.documentSettings.currencySettings.symbol,
        };
      }
    }

    if (state.allTextDefaultStyles) {
      payload.textDefaultStyles = state.allTextDefaultStyles;
    }

    if (state.tableRowStyles) {
      payload.tableDefaultStyles = state.tableRowStyles;
    }

    return payload;
  }

  private publishToSocket(payload: Record<string, any>): Promise<any> {
    return new Promise((resolve) => {
      this.socketClient.publish(DocumentSettingsEvents.DOCUMENT_SETTINGS_SAVE, payload, (response: any) => {
        resolve(response);
      });
    });
  }

  toJSON(): Record<string, any> {
    return {
      previousState: this.previousState,
      newState: this.newState,
    };
  }
}
